import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { X } from 'lucide-react'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/auth-store'
import { queryClient } from '@/utils/main-variables'
import {
  getPreviousAuth,
  clearPreviousAuth,
  isTemporaryLogin,
} from '@/utils/temp-login'
import { Button } from '@/components/ui/button'

/**
 * Banner that shows when user is logged in temporarily as another user
 * Displays at the top center of all pages with option to restore previous account
 */
export function TempLoginBanner() {
  const [show, setShow] = useState(false)
  const [tempUserEmail, setTempUserEmail] = useState<string>('')
  const authStore = useAuthStore()
  const navigate = useNavigate()

  // Check if in temp login mode on mount and when auth changes
  useEffect(() => {
    const checkTempLogin = () => {
      //   const previousAuth = getPreviousAuth()
      const isTemp = isTemporaryLogin()
      setShow(isTemp)

      if (isTemp) {
        // Get current user's email to display
        const currentUser = authStore.auth.getCurrentUser()

        if (currentUser) {
          setTempUserEmail(currentUser.email || '')
        }
      }
    }

    checkTempLogin()

    // Listen for storage changes (in case cleared from another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'temp_login_data') {
        checkTempLogin()
      }
    }

    // Listen for custom event dispatched when temp login happens in same tab
    const handleTempLoginEvent = () => {
      checkTempLogin()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('temp-login-changed', handleTempLoginEvent)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('temp-login-changed', handleTempLoginEvent)
    }
  }, [authStore])

  const restoreMutation = useMutation({
    mutationKey: ['restorePreviousAuth'],
    mutationFn: async () => {
      const previousAuth = getPreviousAuth()
      if (!previousAuth) {
        throw new Error('Aucune session précédente trouvée')
      }
      return previousAuth
    },
    onSuccess: (data) => {
      // Restore previous auth using pb.authStore.save with complete user record
      authStore.auth.restorePreviousAuth(data.previousToken, data.previousUser)

      // Clear temp login data
      clearPreviousAuth()

      // Refresh queries
      queryClient.invalidateQueries()
      queryClient.refetchQueries()

      setShow(false)
      toast.success('Connexion précédente restaurée')

      navigate({ to: '/commands' })
    },
    onError: () => {
      toast.error('Erreur lors de la restauration de la session', {
        testId: 'restore-previous-auth-error-toast',
      })
      clearPreviousAuth()
      setShow(false)
    },
  })

  if (!show) return null

  return (
    <div className='top-0 right-0 left-0 z-[800] flex items-center justify-center bg-amber-500 px-2 py-1.5 text-white shadow-lg sm:px-4 sm:py-1.5'>
      <div className='flex w-full max-w-full flex-col items-center justify-center gap-1.5 text-xs font-medium sm:flex-row sm:gap-2'>
        <span className='text-center sm:text-center'>
          Vous êtes connecté temporairement en tant que{' '}
          <span className='font-semibold'>{tempUserEmail}</span>
        </span>
        <div className='flex items-center gap-1.5'>
          <Button
            variant='secondary'
            size='sm'
            onClick={() => restoreMutation.mutate()}
            disabled={restoreMutation.isPending}
            className='h-6 bg-white px-2 text-xs whitespace-nowrap text-amber-600 hover:bg-amber-50'
          >
            Retour au compte précédent
          </Button>
          <button
            onClick={() => {
              clearPreviousAuth()
              setShow(false)
            }}
            className='ring-offset-background focus:ring-ring rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none'
            aria-label='Fermer'
          >
            <X className='h-3.5 w-3.5' />
          </button>
        </div>
      </div>
    </div>
  )
}
